import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd, buildCollectionPageJsonLd } from "../../utils/jsonld.js";
import NewsletterForm from "../../components/newsletter/NewsletterForm.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import { larpPages, excerpt, estimateReadingTime, getReadableText, getFeaturedLarpPages, findByLangAndId } from "../../utils/content.js";
import { READING_PATH } from "./readingPath.js";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const HUB_IMAGE_SRC = "/assets/brand/blog.png";
const EVENTS_LABEL_KEY = "larp.tryCta";

export default function LarpHub({ lang }) {
  const { t } = useTranslation();
  const [activeTopic, setActiveTopic] = useState(null);
  const pages = larpPages.filter((p) => p.lang === lang);
  // Featured = `featured: true` in frontmatter, most recently created wins
  // if several are flagged — same rule as the homepage teaser
  // (getFeaturedLarpPages), reused here with limit 1. Falls back to the
  // first page (by file order) if nothing is flagged, so the hero never
  // just disappears on a fresh content set.
  const featured = getFeaturedLarpPages(lang, 1)[0] ?? pages[0];
  const rest = pages.filter((p) => p.frontmatter.id !== featured?.frontmatter.id);
  const topics = [...new Set(rest.map((p) => p.frontmatter.topic).filter(Boolean))].sort();
  const visiblePages = activeTopic ? rest.filter((p) => p.frontmatter.topic === activeTopic) : rest;

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/${LARP_PATH[lang]}`}
        altPath={`/${LARP_PATH[lang === "de" ? "en" : "de"]}`}
        title={t("larp.metaTitle")}
        description={t("larp.metaDescription")}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.larpErleben"), path: `/${LARP_PATH[lang]}` },
          ],
          lang
        )}
      />
      <JsonLd
        data={buildCollectionPageJsonLd(
          {
            name: t("larp.title"),
            description: t("larp.body"),
            url: `https://talesandlegends.org/${lang}/${LARP_PATH[lang]}`,
            items: pages.map((p) => ({
              name: p.frontmatter.title,
              url: `https://talesandlegends.org/${lang}/${LARP_PATH[lang]}/${p.frontmatter.slug}`,
            })),
          },
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", paddingTop: "64px", paddingBottom: "96px" }}>
          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr", gap: "40px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <p className="tl-eyebrow" style={{ margin: 0 }}>
                {t("larp.eyebrow")}
              </p>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
                {t("larp.title")}
              </h1>
              <p style={{ fontSize: "17px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("larp.body")}</p>
              <p style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)", margin: 0 }}>{t("larp.intro")}</p>
            </div>
            <div style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", borderRadius: "var(--radius-card-lg)", overflow: "hidden" }}>
              <ResponsiveImage
                src={HUB_IMAGE_SRC}
                alt={t("larp.imageAlt")}
                priority
                sizes="(max-width: 900px) 100vw, 40vw"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>

          {/* Same gold-bordered frame the events and donation blocks use —
            * this is the one section on the page meant to be read in order
            * rather than browsed, so it gets its own box. */}
          <section
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              border: "1px solid rgba(206,149,50,0.55)",
              borderRadius: "var(--radius-card-lg)",
              padding: "36px 40px",
            }}
          >
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
              {t("larp.pathTitle")}
            </h2>
            {/* An ordered list because the order is the point here — each
              * step only makes sense once the one before it does. */}
            <ol style={{ display: "flex", flexDirection: "column", gap: "18px", margin: 0, paddingLeft: "24px", color: "var(--color-cream-soft)" }}>
              {READING_PATH[lang].map((step) => (
                <li key={step.ids[0]} style={{ fontSize: "16px", lineHeight: 1.7 }}>
                  {step.text}
                  <span style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "6px" }}>
                    {step.ids
                      .map((id) => findByLangAndId(larpPages, lang, id))
                      .filter(Boolean)
                      .map((article) => (
                        <Link
                          key={article.frontmatter.id}
                          to={`/${lang}/${LARP_PATH[lang]}/${article.frontmatter.slug}`}
                          style={{ fontSize: "15px", color: "var(--color-gold)" }}
                        >
                          {article.frontmatter.title}
                        </Link>
                      ))}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          {featured && (
            <Link
              to={`/${lang}/${LARP_PATH[lang]}/${featured.frontmatter.slug}`}
              className="tl-card-light tl-hover-lift tl-grid-2"
              style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr" }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "40px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "14px", color: "#8a6520" }}>
                  {`${featured.frontmatter.topic} · ${t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(featured)) })} · ${featured.frontmatter.author}`}
                </span>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "32px", lineHeight: 1.15, color: "var(--color-ink)", maxWidth: "26ch" }}>
                  {featured.frontmatter.title}
                </span>
                <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", maxWidth: "50ch", margin: 0 }}>
                  {featured.frontmatter.subtitle || excerpt(featured.content)}
                </p>
                <span className="tl-pill tl-pill-primary" style={{ alignSelf: "flex-start", padding: "12px 24px", marginTop: "8px" }}>
                  {t("larp.readArticle")}
                </span>
              </div>
              <ResponsiveImage
                src={featured.frontmatter.image}
                alt={featured.frontmatter.imageAlt || featured.frontmatter.title}
                variant="light"
                style={{ width: "100%", height: "100%", minHeight: "260px", objectFit: "cover" }}
              />
            </Link>
          )}

          {topics.length > 0 && (
            <div role="group" aria-label={t("larp.filterByTopic")} style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setActiveTopic(null)}
                className={`tl-pill ${activeTopic === null ? "tl-pill-primary" : "tl-pill-outline"}`}
                style={{ padding: "9px 18px", fontSize: "13px" }}
              >
                {t("larp.allTopics")}
              </button>
              {topics.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => setActiveTopic(topic)}
                  aria-pressed={activeTopic === topic}
                  className={`tl-pill ${activeTopic === topic ? "tl-pill-primary" : "tl-pill-outline"}`}
                  style={{ padding: "9px 18px", fontSize: "13px" }}
                >
                  {topic}
                </button>
              ))}
            </div>
          )}

          {rest.length > 0 && visiblePages.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("larp.noTopicResults")}</p>
          )}

          <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "28px", color: "var(--color-cream)", margin: 0 }}>
            {t("larp.allGuidesTitle")}
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "24px" }}>
            {visiblePages.map((page) => (
              <Link
                key={page.frontmatter.id}
                to={`/${lang}/${LARP_PATH[lang]}/${page.frontmatter.slug}`}
                className="tl-card tl-hover-raise"
                style={{ display: "flex", flexDirection: "column", gap: "12px", minHeight: "240px" }}
              >
                <span className="tl-eyebrow" style={{ fontSize: "12px" }}>
                  {`${page.frontmatter.topic} · ${t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(page)) })}`}
                </span>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "21px", lineHeight: 1.25, color: "var(--color-cream)" }}>
                  {page.frontmatter.title}
                </span>
                <span style={{ fontSize: "16px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>
                  {page.frontmatter.subtitle || excerpt(page.content)}
                </span>
                <span style={{ fontSize: "13px", color: "var(--color-cream)", marginTop: "4px" }}>{t("larp.readMore")}</span>
              </Link>
            ))}

            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "28px", minHeight: "240px", gridColumn: "-2", gridRow: "1" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "21px", lineHeight: 1.25, color: "var(--color-ink)" }}>
                {t("nav.newsletter")}
              </span>
              <div style={{ marginTop: "4px" }}>
                <NewsletterForm lang={lang} source="LARPHub" variant="steps" />
              </div>
            </div>
          </div>

          <section
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "32px",
              flexWrap: "wrap",
              border: "1px solid rgba(206,149,50,0.55)",
              borderRadius: "14px",
              padding: "32px 36px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: "1 1 420px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", lineHeight: 1.2, color: "var(--color-cream)", margin: 0 }}>
                {t("larp.tryTitle")}
              </h2>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("larp.tryBody")}</p>
            </div>
            <Link to={`/${lang}/events`} className="tl-pill tl-pill-primary" style={{ padding: "15px 30px" }}>
              {t(EVENTS_LABEL_KEY)}
            </Link>
          </section>
        </div>
      </div>
    </>
  );
}
